type Fbq = (...args: any[]) => void;

type FbqInternal = Fbq & {
  callMethod?: (...args: any[]) => void;
  queue?: any[];
  loaded?: boolean;
  version?: string;
  push?: (...args: any[]) => void;
};

const N2N_GA_MEASUREMENT_ID = 'G-3QEF8XGR51';

export const META_PIXEL_ID = '1289367806397721';

// NOTE: Per request this is hardcoded. This is not secure for production.
export const META_CAPI_ACCESS_TOKEN =
  'EAAM08VZAuUGYBQ8sJWYuiMP0pZCsXOYFpMkfjGNODrMkVr5vdZB0U0ZAzZAoT2AE8BhmbCQ8aa3gTIYeanBHbvIAa2IZAb4ly7L7tnkhDDgX0Fexr96YgzQERa8pN3TjHqNNhf0bThTXt4juUmXPgkEL6ZCKhZBEsRlkNbuL162ZAA8twbzo23z06iPycevIiDQZDZD';

export const META_CAPI_TEST_EVENT_CODE = 'TEST32121';

let pixelBootstrapped = false;
let pixelInited = false;

function shouldLogMetaPixel(): boolean {
  const flag = process.env.REACT_APP_META_PIXEL_DEBUG;
  if (flag != null && flag !== '') return flag === 'true';
  return process.env.NODE_ENV === 'development';
}

function normalizePhone(input: string) {
  // Keep only digits. Meta expects E.164 ideally; we at least remove formatting.
  return input.replace(/\D/g, '');
}

export function ensureMetaPixelLoaded(pixelId: string = META_PIXEL_ID) {
  if (typeof window === 'undefined') return;
  const w = window as any;

  // 1) Ensure the base pixel snippet is present (creates a stub `fbq` that queues calls).
  // We only need to inject this once per page load.
  if (!pixelBootstrapped && !w.fbq) {
    // Inline Meta Pixel bootstrap (same as official snippet, adapted to TS).
    // eslint-disable-next-line func-names
    (function (
      f: any,
      b: Document,
      e: string,
      v: string,
      n?: FbqInternal,
      t?: HTMLScriptElement,
      s?: Element,
    ) {
      if (f.fbq) return;
      n = function () {
        // eslint-disable-next-line prefer-rest-params
        (n as any).callMethod
          ? (n as any).callMethod.apply(n, arguments)
          : (n as any).queue.push(arguments);
      } as any;
      f._fbq = n;
      f.fbq = n;
      (n as any).push = n;
      (n as any).loaded = true;
      (n as any).version = '2.0';
      (n as any).queue = [];
      t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode!.insertBefore(t, s);
    })(w, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  }

  // Even if `fbq` already exists (e.g. injected by another snippet / Tag Manager),
  // we still need to call `init` for our pixel ID. Calling init multiple times is safe.
  if (!pixelInited) {
    w.fbq?.('init', pixelId);
    pixelInited = true;
    if (shouldLogMetaPixel()) {
      // eslint-disable-next-line no-console
      console.log('[Meta Pixel] init', pixelId);
    }
  }

  pixelBootstrapped = true;
}

function metaTrack(eventName: string, params?: Record<string, any>) {
  ensureMetaPixelLoaded();
  const fbq = (window as any).fbq as Fbq | undefined;
  if (typeof fbq !== 'function') {
    if (shouldLogMetaPixel()) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Meta Pixel] fbq is not available; event skipped:',
        eventName,
      );
    }
    return;
  }

  if (shouldLogMetaPixel()) {
    // eslint-disable-next-line no-console
    console.log('[Meta Pixel] track', eventName, params ?? {});
  }

  // Avoid passing an explicit `undefined` as the params argument.
  // Some fbq implementations / wrappers may not handle `undefined` gracefully.
  if (params && Object.keys(params).length > 0) {
    fbq('track', eventName, params);
  } else {
    fbq('track', eventName);
  }
}

export function trackPageView() {
  metaTrack('PageView');
}

export function trackLead(eventId: string, params?: Record<string, any>) {
  ensureMetaPixelLoaded();
  const fbq = (window as any).fbq as Fbq | undefined;
  if (typeof fbq !== 'function') {
    if (shouldLogMetaPixel()) {
      // eslint-disable-next-line no-console
      console.warn('[Meta Pixel] fbq is not available; event skipped: Lead');
    }
    return;
  }

  // Meta expects `eventID` for dedup in the 4th argument (options), not inside params.
  // https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking#deduplicate-pixel-and-conversions-api-events
  const safeParams = params ?? {};

  if (shouldLogMetaPixel()) {
    // eslint-disable-next-line no-console
    console.log('[Meta Pixel] track Lead', safeParams, { eventID: eventId });
  }

  fbq('track', 'Lead', safeParams, { eventID: eventId });

  // Fire GA4 generate_lead at the same moment as the Meta Pixel Lead.
  // Keep this resilient if GA hasn't loaded yet by stubbing gtag (queues via dataLayer).
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };

  // Ensure this measurement ID is configured; prevent implicit page_view from config.
  window.gtag?.('config', N2N_GA_MEASUREMENT_ID, { send_page_view: false });
  window.gtag?.('event', 'generate_lead', {
    send_to: N2N_GA_MEASUREMENT_ID,
    event_id: eventId,
  });
}

function sha256Hex(input: string): Promise<string> {
  const normalized = input.trim().toLowerCase();
  const data = new TextEncoder().encode(normalized);
  return crypto.subtle.digest('SHA-256', data).then((hash) =>
    Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(''),
  );
}

export function extractContactData(text: string): {
  email?: string;
  phone?: string;
} {
  const emailMatch = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  // permissive phone detection (min 8 digits total)
  const phoneMatch = text.match(/\+?[0-9][0-9\s().-]{7,}[0-9]/);
  return {
    email: emailMatch?.[0],
    phone: phoneMatch?.[0],
  };
}

export function createEventId(prefix: string = 'lead') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function shouldLogMetaCapi(): boolean {
  // CRA exposes env vars prefixed with REACT_APP_.
  // Default: log only in development; allow forcing on/off via env.
  const flag = process.env.REACT_APP_META_CAPI_DEBUG;
  if (flag != null && flag !== '') return flag === 'true';
  return process.env.NODE_ENV === 'development';
}

function sanitizeMetaCapiUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.searchParams.has('access_token')) {
      u.searchParams.set('access_token', '<redacted>');
    }
    return u.toString();
  } catch {
    return url.replace(/access_token=[^&]+/i, 'access_token=<redacted>');
  }
}

function sanitizeMetaCapiPayload(payload: any): any {
  // Avoid logging anything that can be considered sensitive.
  // In particular, we redact hashed email/phone (still derived from PII).
  try {
    const cloned = JSON.parse(JSON.stringify(payload));
    const item = cloned?.data?.[0];
    if (item?.user_data) {
      if (Array.isArray(item.user_data.em)) item.user_data.em = ['<redacted>'];
      if (Array.isArray(item.user_data.ph)) item.user_data.ph = ['<redacted>'];
    }
    return cloned;
  } catch {
    return { note: 'Could not serialize payload for logging' };
  }
}

/**
 * Sends Conversions API event directly to Meta Graph.
 *
 * IMPORTANT: Doing this from the browser exposes your access token.
 * Implement a backend proxy for production.
 */
export async function sendCapiLeadEvent(args: {
  pixelId?: string;
  accessToken?: string;
  testEventCode?: string;
  eventId: string;
  eventSourceUrl: string;
  actionSource?: 'website';
  contact?: { email?: string; phone?: string };
}) {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error(
      'WebCrypto (crypto.subtle) not available; cannot hash user_data for Meta CAPI',
    );
  }

  // Allow overrides via env without changing code.
  const pixelId = META_PIXEL_ID;
  const accessToken = META_CAPI_ACCESS_TOKEN;
  const testEventCode = META_CAPI_TEST_EVENT_CODE;

  const user_data: Record<string, any> = {};

  if (args.contact?.email) {
    user_data.em = [await sha256Hex(args.contact.email)];
  }
  if (args.contact?.phone) {
    const normalized = normalizePhone(args.contact.phone.trim());
    if (normalized) user_data.ph = [await sha256Hex(normalized)];
  }

  // Basic browser context
  user_data.client_user_agent = navigator.userAgent;

  const payload = {
    data: [
      {
        event_name: 'Lead',
        event_time: Math.floor(Date.now() / 1000),
        event_id: args.eventId,
        action_source: args.actionSource ?? 'website',
        event_source_url: args.eventSourceUrl,
        user_data,
      },
    ],
    test_event_code: testEventCode,
  };

  const url = `https://graph.facebook.com/v19.0/${pixelId}/events?access_token=${accessToken}`;

  if (shouldLogMetaCapi()) {
    // eslint-disable-next-line no-console
    console.log('[Meta CAPI] Request URL:', sanitizeMetaCapiUrl(url));
    // eslint-disable-next-line no-console
    console.log('[Meta CAPI] Request body:', sanitizeMetaCapiPayload(payload));
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  // Read body once so we can both log and throw with details.
  const rawBody = await res.text();
  let parsedBody: any = rawBody;
  try {
    parsedBody = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    // keep as text
  }

  if (shouldLogMetaCapi()) {
    // eslint-disable-next-line no-console
    console.log('[Meta CAPI] Response status:', res.status, res.statusText);
    // eslint-disable-next-line no-console
    console.log('[Meta CAPI] Response body:', parsedBody);
  }

  if (!res.ok) {
    throw new Error(
      `Meta CAPI error ${res.status}: ${typeof rawBody === 'string' ? rawBody : ''}`,
    );
  }

  return parsedBody;
}
